/**
 * How Cocon requests are processed, week by week — rebuilt from the board's activity log.
 *
 * Ubi, 26 Oct 2026 (relayed by Hugues): *"number of tickets closed in the last 7 days per
 * subco, and show running stats how the tickets are processed (pending, closed, etc)"*, per
 * company and per group. Hugues: *"there needs to be a pending action count per partner"*,
 * and *"each subco has to be able to see his own stats because these numbers will be used in
 * weekly meetings between Ubi and the partners"*. Decided with him
 * (docs/stats-proposal-2026-10-26.md):
 *
 *   · ARCHIVED and deleted tickets are left out entirely — in no week, open or closed. The
 *     tickets counted are the board's active tickets at the morning read.
 *   · A ticket counts for the status, company and group it had AT THE TIME: for each partner
 *     it was assigned to, for the time it was theirs.
 *   · No renaming. A ticket with no status counts as the label the board shows for it ("On
 *     hold"), one with no company as "Unifiber" — Monday's default labels.
 *   · Weeks run Monday to Sunday; a week ends at Monday 00:00 Brussels.
 *   · Closed = the status moved INTO Done; reopened = moved out of it. These are events, not
 *     tickets: a ticket closed, reopened and closed again in one week counts twice.
 *   · "Closed in the last 7 days" is rolling, up to the morning read.
 *
 * ⚠ Monday keeps no "closed on" date, and the status column's `changed_at` is only the LAST
 * change. The board's activity log keeps every change back to the board's first day (30 May
 * 2025); this rebuilds each ticket's status, company and group at any moment from it. Two
 * things in the log must be ignored or every change counts twice: `batch_*` events repeat
 * the per-ticket events of a bulk edit, and `move_pulse_into_group` repeats
 * `move_pulse_from_group`.
 *
 * ⚠ A rebuild that does not reproduce the live board is not shipped silently (hard rule 30).
 * `controls` checks it every morning: every change starts from the value the previous change
 * left, today's rebuilt board equals the board as read, every week adds up per partner, and
 * each week starts where the last one ended. The screen says so in amber when one fails.
 *
 * ⚠ Computed ONCE, at the morning job, and stored — like the installation flow on Order
 * Activation. The numbers are argued over in a meeting, so they must read the same for Ubi
 * and for the partner all day.
 */
import { CLOSED_STATUS, matchesMondayScope } from "./monday.js";
import { businessDate, dateOnly, isoWeekKey, zonedTimeToUtc } from "./time.js";
export const HISTORY_TIMEZONE = "Europe/Brussels";
export const PENDING_PARTNER_STATUS = "Pending action by Partner";
const DAY = 86_400_000;
/**
 * The label index a column value names, mirroring every shape the log has used since 2025.
 *
 * Undefined when the log does not carry the value at all; null for an empty value — and,
 * as in the measurement this was validated by, for a shape it does not recognise.
 */
function labelIndex(value) {
    if (value === undefined)
        return undefined;
    if (value === null || typeof value !== "object" || Object.keys(value).length === 0)
        return null;
    const v = value;
    if (v.label && typeof v.label === "object") {
        const index = v.label.index;
        return typeof index === "number" ? index : null;
    }
    if (typeof v.index === "number")
        return v.index;
    return null;
}
/** One log entry → the change it records, or why there is none. */
export function parseActivityLog(raw, columns) {
    // Duplicates of per-ticket events: counting them would count every bulk edit twice.
    if (raw.event.startsWith("batch_") || raw.event === "move_pulse_into_group")
        return "ignored";
    if (raw.event !== "update_column_value" && raw.event !== "create_pulse" && raw.event !== "move_pulse_from_group") {
        return "ignored";
    }
    let d;
    try {
        d = JSON.parse(raw.data);
    }
    catch {
        return "unreadable";
    }
    if (raw.event === "update_column_value" && d.column_id !== columns.status && d.column_id !== columns.company) {
        return "ignored";
    }
    const pulse = d.pulse;
    const itemId = d.pulse_id ?? pulse?.id ?? d.item_id;
    if ((typeof itemId !== "number" && typeof itemId !== "string") || !/^\d{1,20}$/.test(String(itemId))) {
        return "unreadable";
    }
    if (!/^\d{1,20}$/.test(raw.created_at))
        return "unreadable";
    const base = { id: raw.id, at: raw.created_at, itemId: String(itemId) };
    if (raw.event === "update_column_value") {
        const event = { ...base, kind: d.column_id === columns.status ? "status" : "company" };
        const prev = labelIndex(d.previous_value);
        const next = labelIndex(d.value);
        if (prev !== undefined)
            event.prev = prev;
        if (next !== undefined)
            event.next = next;
        return event;
    }
    if (raw.event === "create_pulse") {
        const event = { ...base, kind: "create" };
        if (typeof d.group_id === "string")
            event.group = d.group_id;
        if (typeof d.group_name === "string")
            event.groupTitle = d.group_name;
        let values = null;
        try {
            values = typeof d.column_values_json === "string" ? JSON.parse(d.column_values_json) : {};
        }
        catch {
            values = null; // unreadable: the initial values are then taken from the first change
        }
        if (values) {
            // A column absent from the creation payload was not set.
            const status = columns.status in values ? labelIndex(values[columns.status]) : null;
            const company = columns.company in values ? labelIndex(values[columns.company]) : null;
            if (status !== undefined)
                event.status = status;
            if (company !== undefined)
                event.company = company;
        }
        return event;
    }
    const source = d.source_group;
    const dest = d.dest_group;
    const titles = {};
    for (const g of [source, dest]) {
        if (typeof g?.id === "string" && typeof g.title === "string")
            titles[g.id] = g.title;
    }
    return {
        ...base,
        kind: "group",
        prev: typeof source?.id === "string" ? source.id : null,
        next: typeof dest?.id === "string" ? dest.id : null,
        titles,
    };
}
/** Monday's `created_at` digits → milliseconds. */
export function logTimeMs(at) {
    return Number(at.slice(0, -4) || "0");
}
/** Milliseconds → the digits `created_at` would carry, for asking the log "since when". */
export function msToLogTime(ms) {
    return `${Math.floor(ms)}0000`;
}
function compareAt(a, b) {
    return a.length - b.length || (a < b ? -1 : a > b ? 1 : 0);
}
function timelines(tickets, events, asOf) {
    const byId = new Map();
    for (const ticket of tickets) {
        byId.set(ticket.id, {
            id: ticket.id,
            created: Date.parse(ticket.createdAt),
            hasCreate: false,
            init: { status: undefined, company: undefined, group: undefined },
            status: [],
            company: [],
            group: [],
            now: { status: ticket.status, company: ticket.company, group: ticket.groupId },
        });
    }
    const created = new Map();
    for (const e of [...events].sort((a, b) => compareAt(a.at, b.at))) {
        const tl = byId.get(e.itemId);
        if (!tl)
            continue; // archived or deleted since: left out entirely
        const t = logTimeMs(e.at);
        if (t > asOf)
            continue;
        if (e.kind === "create")
            created.set(e.itemId, e);
        else if (e.kind === "group")
            tl.group.push({ t, prev: e.prev, next: e.next ?? undefined, noSource: e.prev === null });
        else
            tl[e.kind].push({ t, prev: e.prev, next: e.next });
    }
    for (const tl of byId.values()) {
        const c = created.get(tl.id);
        tl.hasCreate = c !== undefined;
        // The creation payload, else what the first change says it changed from, else today's value.
        tl.init.status = c?.status !== undefined ? c.status : tl.status[0] ? tl.status[0].prev : tl.now.status;
        tl.init.company = c?.company !== undefined ? c.company : tl.company[0] ? tl.company[0].prev : tl.now.company;
        const firstMove = tl.group.find((s) => !s.noSource);
        tl.init.group = c?.group !== undefined ? c.group : firstMove ? firstMove.prev : tl.now.group;
    }
    return [...byId.values()];
}
/** The value a ticket had at a moment; undefined before it existed. */
function valueAt(tl, field, t) {
    if (t < tl.created)
        return undefined;
    let v = tl.init[field];
    for (const s of tl[field]) {
        if (s.t > t)
            break;
        v = s.next;
    }
    return v;
}
function addDays(iso, n) {
    return new Date(dateOnly(iso).getTime() + n * DAY).toISOString().slice(0, 10);
}
function mondayOf(iso) {
    return addDays(iso, -((dateOnly(iso).getUTCDay() + 6) % 7));
}
function midnight(iso) {
    return zonedTimeToUtc(iso, 0, 0, HISTORY_TIMEZONE).getTime();
}
/**
 * The same Brussels clock time, `days` calendar days earlier. "The last 7 days" read on
 * Monday 26 Oct at 06:31 start on 19 Oct at 06:31 — not at 07:31, which is where 7 × 24 hours
 * lands across the clock change of 25 Oct.
 */
function sameTimeDaysBefore(t, days) {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: HISTORY_TIMEZONE, hour12: false, hour: "2-digit", minute: "2-digit" }).formatToParts(new Date(t));
    const num = (type) => Number(parts.find((p) => p.type === type)?.value ?? "0");
    const day = addDays(businessDate(new Date(t), HISTORY_TIMEZONE), -days);
    // Zone offsets are whole minutes, so the seconds within the minute carry over unchanged.
    return zonedTimeToUtc(day, num("hour") % 24, num("minute"), HISTORY_TIMEZONE).getTime() + (t % 60_000);
}
/** Does a partner's week add up? open at start + created + reopened − closed ± relabelled = open at end. */
export function addsUp(f) {
    return f.openStart + f.createdOpen + f.reopened - f.closed + f.relabelIn - f.relabelOut === f.openEnd;
}
function emptyFlow() {
    return { openStart: 0, createdOpen: 0, createdDone: 0, reopened: 0, closed: 0, relabelIn: 0, relabelOut: 0, openEnd: 0, pendingEnd: 0 };
}
function countInto(map, key) {
    const k = JSON.stringify(key);
    const cell = map.get(k);
    if (cell)
        cell[cell.length - 1] = cell[cell.length - 1] + 1;
    else
        map.set(k, [...key, 1]);
}
/**
 * The board's whole history, from its first week to the read.
 *
 * `tickets` are the ACTIVE tickets as read — so archived and deleted ones are left out of
 * every week, as decided. `events` are every stored log entry; those after `asOf` wait for
 * tomorrow, so the read and the log describe the same moment.
 */
export function requestHistory(input) {
    const asOf = Date.parse(input.asOf);
    const voc = input.vocabulary;
    const all = timelines(input.tickets, input.events, asOf);
    // A group deleted since keeps the name the log gave it.
    const groupTitles = {};
    for (const e of input.events) {
        if (e.kind === "create" && e.group && e.groupTitle)
            groupTitles[e.group] = e.groupTitle;
        if (e.kind === "group")
            Object.assign(groupTitles, e.titles);
    }
    Object.assign(groupTitles, voc.groupTitles);
    const statusName = (i) => i === null ? voc.statusDefault : i === undefined ? "(unknown)" : (voc.statusLabels[String(i)] ?? `(status ${i})`);
    const companyName = (i) => i === null ? voc.companyDefault : i === undefined ? "(unknown)" : (voc.companyLabels[String(i)] ?? `(company ${i})`);
    const groupName = (g) => (g ? (groupTitles[g] ?? "(deleted group)") : "(no group)");
    const statusAt = (tl, t) => statusName(valueAt(tl, "status", t));
    const companyAt = (tl, t) => companyName(valueAt(tl, "company", t));
    const groupAt = (tl, t) => groupName(valueAt(tl, "group", t));
    const isDone = (label) => label === CLOSED_STATUS;
    const cellsAt = (t) => {
        const cells = new Map();
        for (const tl of all) {
            if (tl.created > t)
                continue;
            countInto(cells, [statusAt(tl, t), companyAt(tl, t), groupAt(tl, t)]);
        }
        return [...cells.values()].sort((a, b) => b[3] - a[3] || a.join().localeCompare(b.join()));
    };
    /** One week, (start, end]. */
    const flowOf = (s, e) => {
        const byPartner = {};
        const of = (company) => (byPartner[company] ??= emptyFlow());
        const closed = new Map();
        for (const tl of all) {
            if (tl.created > e)
                continue;
            if (tl.created <= s) {
                if (!isDone(statusAt(tl, s)))
                    of(companyAt(tl, s)).openStart++;
            }
            else {
                const initial = statusName(tl.init.status);
                if (isDone(initial))
                    of(companyName(tl.init.company)).createdDone++;
                else
                    of(companyName(tl.init.company)).createdOpen++;
            }
            for (const step of tl.status) {
                if (step.t <= s)
                    continue;
                if (step.t > e)
                    break;
                const was = isDone(statusName(step.prev));
                const now = isDone(statusName(step.next));
                if (now && !was) {
                    const company = companyAt(tl, step.t);
                    of(company).closed++;
                    countInto(closed, [company, groupAt(tl, step.t)]);
                }
                else if (was && !now)
                    of(companyAt(tl, step.t)).reopened++;
            }
            for (const step of tl.company) {
                if (step.t <= s)
                    continue;
                if (step.t > e)
                    break;
                // A relabel moves the open count between partners; a Done ticket's does not.
                if (step.prev === step.next || isDone(statusAt(tl, step.t)))
                    continue;
                of(companyName(step.prev)).relabelOut++;
                of(companyName(step.next)).relabelIn++;
            }
            const statusEnd = statusAt(tl, e);
            if (!isDone(statusEnd)) {
                const f = of(companyAt(tl, e));
                f.openEnd++;
                if (statusEnd === PENDING_PARTNER_STATUS)
                    f.pendingEnd++;
            }
        }
        return { byPartner, closed: [...closed.values()].sort((a, b) => b[2] - a[2] || a.join().localeCompare(b.join())) };
    };
    // ── Every week, from the Monday of the first ticket to the read ──
    const weeks = [];
    const first = all.length ? Math.min(...all.map((tl) => tl.created)) : asOf;
    for (let monday = mondayOf(businessDate(new Date(first), HISTORY_TIMEZONE));; monday = addDays(monday, 7)) {
        const s = midnight(monday);
        if (s >= asOf)
            break;
        const next = midnight(addDays(monday, 7));
        const toDate = next > asOf;
        const e = toDate ? asOf : next;
        weeks.push({
            week: isoWeekKey(dateOnly(monday)),
            start: monday,
            end: addDays(monday, 6),
            toDate,
            endsAt: new Date(e).toISOString(),
            cells: cellsAt(e),
            ...flowOf(s, e),
        });
        if (toDate)
            break;
    }
    // ── Now ──
    const closed7 = new Map();
    const from7 = sameTimeDaysBefore(asOf, 7);
    const today = businessDate(new Date(asOf), HISTORY_TIMEZONE);
    const waiting = {};
    for (const tl of all) {
        for (const step of tl.status) {
            if (step.t <= from7 || step.t > asOf)
                continue;
            if (isDone(statusName(step.next)) && !isDone(statusName(step.prev))) {
                countInto(closed7, [companyAt(tl, step.t), groupAt(tl, step.t)]);
            }
        }
        if (statusAt(tl, asOf) !== PENDING_PARTNER_STATUS)
            continue;
        // Since the ticket last entered the status — or since it was created in it.
        let entered = statusName(tl.init.status) === PENDING_PARTNER_STATUS ? tl.created : null;
        for (const step of tl.status)
            if (step.t <= asOf && statusName(step.next) === PENDING_PARTNER_STATUS)
                entered = step.t;
        if (entered === null)
            entered = tl.created;
        // Calendar days in Brussels, like every age in this application: in on 2 Sep, read on 26 Oct = 54.
        (waiting[companyAt(tl, asOf)] ??= []).push(Math.round((dateOnly(today).getTime() - dateOnly(businessDate(new Date(entered), HISTORY_TIMEZONE)).getTime()) / DAY));
    }
    for (const days of Object.values(waiting))
        days.sort((a, b) => a - b);
    // ── Controls ──
    let chainBreaks = 0;
    let rebuiltNotBoard = 0;
    for (const tl of all) {
        for (const field of ["status", "company", "group"]) {
            let left = tl.init[field];
            for (const step of tl[field]) {
                if (!step.noSource && step.prev !== left)
                    chainBreaks++;
                left = step.next;
            }
        }
        if (valueAt(tl, "status", asOf) !== tl.now.status ||
            valueAt(tl, "company", asOf) !== tl.now.company ||
            valueAt(tl, "group", asOf) !== tl.now.group) {
            rebuiltNotBoard++;
        }
    }
    const weeksNotAddingUp = [];
    const weeksNotChained = [];
    const openTotal = (w, k) => Object.values(w.byPartner).reduce((sum, f) => sum + f[k], 0);
    weeks.forEach((w, i) => {
        for (const [company, f] of Object.entries(w.byPartner))
            if (!addsUp(f))
                weeksNotAddingUp.push(`${w.week} · ${company}`);
        if (i > 0 && openTotal(w, "openStart") !== openTotal(weeks[i - 1], "openEnd"))
            weeksNotChained.push(w.week);
    });
    // ── Dimensions: board order first, then anything else A–Z ──
    const seen = { statuses: new Set(), companies: new Set(), groups: new Set() };
    const note = (cells) => {
        for (const [st, co, gr] of cells) {
            seen.statuses.add(st);
            seen.companies.add(co);
            seen.groups.add(gr);
        }
    };
    for (const w of weeks) {
        note(w.cells);
        for (const company of Object.keys(w.byPartner))
            seen.companies.add(company);
    }
    const now = cellsAt(asOf);
    note(now);
    const ordered = (values, boardOrder) => {
        const order = Object.entries(boardOrder)
            .sort((a, b) => Number(a[0]) - Number(b[0]))
            .map(([, label]) => label);
        return [...values].sort((a, b) => (order.indexOf(a) + 1 || 999) - (order.indexOf(b) + 1 || 999) || a.localeCompare(b));
    };
    return {
        asOf: new Date(asOf).toISOString(),
        statuses: ordered(seen.statuses, voc.statusLabels),
        companies: ordered(seen.companies, voc.companyLabels),
        groups: [...seen.groups].sort((a, b) => a.localeCompare(b)),
        weeks,
        now,
        closedLast7: {
            from: new Date(from7).toISOString(),
            to: new Date(asOf).toISOString(),
            cells: [...closed7.values()].sort((a, b) => b[2] - a[2] || a.join().localeCompare(b.join())),
        },
        waiting,
        controls: {
            tickets: all.length,
            events: input.events.length,
            ticketsWithoutCreateEvent: all.filter((tl) => !tl.hasCreate).length,
            chainBreaks,
            rebuiltNotBoard,
            weeksNotAddingUp,
            weeksNotChained,
        },
    };
}
// ─────────────────────────────────────────────────────────────────────────────
// Who sees what
// ─────────────────────────────────────────────────────────────────────────────
/**
 * The history as one viewer may see it.
 *
 * ⚠ The same `matchesMondayScope` as every row of the Cocon Requests report (hard rule 9):
 * a contractor receives the cells, flows, closes and waiting times of their own labels and
 * nothing else — not even the NAMES of the other partners, so the dimension lists are rebuilt
 * from what is left. A ticket with no company shows as "Unifiber" and reaches internal users
 * only, as it does on the live board.
 */
export function scopeRequestHistory(history, labels) {
    if (labels === null)
        return history;
    const mine = (company) => matchesMondayScope(company, labels);
    const ownCells = (cells) => cells.filter((c) => mine(c[1]));
    const ownClosed = (cells) => cells.filter((c) => mine(c[0]));
    const ownKeys = (record) => Object.fromEntries(Object.entries(record).filter(([k]) => mine(k)));
    const weeks = history.weeks.map((w) => ({ ...w, cells: ownCells(w.cells), byPartner: ownKeys(w.byPartner), closed: ownClosed(w.closed) }));
    const now = ownCells(history.now);
    const used = { statuses: new Set(), companies: new Set(), groups: new Set() };
    for (const cells of [...weeks.map((w) => w.cells), now]) {
        for (const [st, co, gr] of cells) {
            used.statuses.add(st);
            used.companies.add(co);
            used.groups.add(gr);
        }
    }
    for (const w of weeks)
        for (const c of Object.keys(w.byPartner))
            used.companies.add(c);
    return {
        asOf: history.asOf,
        statuses: history.statuses.filter((s) => used.statuses.has(s)),
        companies: history.companies.filter((c) => used.companies.has(c)),
        groups: history.groups.filter((g) => used.groups.has(g)),
        weeks,
        now,
        closedLast7: { ...history.closedLast7, cells: ownClosed(history.closedLast7.cells) },
        waiting: ownKeys(history.waiting),
        controls: {
            ...history.controls,
            // Another partner's failing weeks name that partner: a contractor sees their own.
            weeksNotAddingUp: history.controls.weeksNotAddingUp.filter((k) => mine(k.split(" · ").slice(1).join(" · "))),
            weeksNotChained: [],
        },
    };
}
/** Whether the rebuild can be trusted, for the amber line on screen. */
export function historyReconciles(controls) {
    return (controls.chainBreaks === 0 &&
        controls.rebuiltNotBoard === 0 &&
        controls.weeksNotAddingUp.length === 0 &&
        controls.weeksNotChained.length === 0 &&
        controls.defaultsAgree !== false);
}
/** Waiting times → the four age bands and the median, as the "Pending action by partner" table shows them. */
export function waitingSummary(days) {
    const sorted = [...days].sort((a, b) => a - b);
    const n = sorted.length;
    return {
        n,
        lt7: sorted.filter((d) => d < 7).length,
        d7to30: sorted.filter((d) => d >= 7 && d < 30).length,
        d30to90: sorted.filter((d) => d >= 30 && d < 90).length,
        over90: sorted.filter((d) => d >= 90).length,
        // For an even count the mean of the two middle values, rounded: a whole number of days.
        median: n ? (n % 2 ? sorted[(n - 1) / 2] : Math.round((sorted[n / 2 - 1] + sorted[n / 2]) / 2)) : null,
        oldest: n ? sorted[n - 1] : null,
    };
}
//# sourceMappingURL=request-history.js.map